import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import CustomLoading from '../../util_components/custom_loading';
import CommonHeader from '../../util_components/common_header';
import * as actions from '../../../redux/action_creators';
import CustomModal from '../../util_components/custom_modal';
import CustomInput from '../../util_components/custom_input';
import CustomSelect from '../../util_components/custom_single_select';
import { get_api_url } from '../../../utils/urls';
import { get_api } from '../../../redux/api_funcs';
import HighlightSearch from '../../util_components/highlight_search_text';
import EventViewer from './EventViewer';
import { day_format_full_year, time_format } from '../../../constants';
import { FrameWrapper } from './FrameWrapper';
import StatusCodeLegend from './StatusCodeLegend';
import { cleanMessageHtml, textToHtmlBody } from '../support_inbox/api_helpers';
import {
  careRows,
  carePage,
  careTotalPages,
  ownsConversation,
  searchParamSets,
  uniqueHits
} from './care_rows';

// '' is "all" for both filters: CustomSelect renders it as the default option,
// and goserver's timelineFilterSQL treats '' as no predicate.
const DIRECTION_OPTIONS = [
  { value: 'incoming', label: 'Incoming' },
  { value: 'outgoing', label: 'Outgoing' }
];

// 'care' is hridaya-only: it switches the source to the Support Inbox APIs and
// is never sent to the ClickHouse timeline, which would 400 on it.
const CARE = 'care';
const CATEGORY_OPTIONS = [
  { value: 'transactional', label: 'Transactional' },
  { value: 'zendesk', label: 'Zendesk' },
  { value: CARE, label: 'Care team (Support Inbox)' }
];

function EmailTimeline() {
  const [loading, setLoading] = useState(false);
  const [showStatusLog, setShowStatusLog] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userUUID, setUserUUID] = useState('');
  const [timelineData, setTimelineData] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [rawDataSearchInput, setRawDataSearchInput] = useState('');
  const [pageTitle, setPageTitle] = useState('Emails timeline');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userGeoInfos, setUserGeoInfos] = useState({});
  const [selectedGeoIndex, setSelectedGeoIndex] = useState(-1);
  const [direction, setDirection] = useState('');
  const [category, setCategory] = useState('');
  // Every Support Inbox thread for this person, fetched once on first open of
  // the Care team category and then filtered and paged in memory: Care's brief
  // on STAFFPROJE-95 is that a student has only a handful of tickets.
  const [careThreads, setCareThreads] = useState(null);
  const [careError, setCareError] = useState('');

  // Rapid filter changes put several timeline requests in flight; only the
  // latest may write state, or a slow earlier response would show its rows
  // under the newer filter's label.
  const requestSeq = useRef(0);

  const history = useHistory();
  const location = useLocation();
  // Passed by the student and teacher profile buttons as router state (the
  // /createemail precedent in teacher_profile), not in the URL, so the address
  // stays out of browser history and server logs. BrowserRouter keeps it across
  // a reload; a pasted link has none, and the Care team view says so.
  const contactEmail = location.state?.email || '';

  const dispatch = useDispatch();

  const iana_timezone = useSelector((state) => state.home.iana_timezone);

  const goToPath = (path) => history.push(path);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (category !== CARE) loadData(page);
  };

  const fetchUserGeoInfo = async (ip, index) => {
    if (userGeoInfos[ip]) {
      setSelectedGeoIndex(index);
      return;
    }
    const url = `https://geo-tag.azure-services.myyogateacher.com/ip?ip=${ip}`;

    get_api(url).then((result) => {
      setUserGeoInfos((prevGeoInfos) => ({
        ...prevGeoInfos,
        [ip]: result.data
      }));
      setSelectedGeoIndex(index);
    });
  };

  const sortData = (data = []) => {
    // Sort timelineData by created_date in descending order
    const sortedData = data.slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return sortedData;
  };

  const loadData = (pageNo = 1, dir = direction, cat = category) => {
    try {
      if (userUUID !== '' && userRole !== '') {
        setLoading(true);
        requestSeq.current += 1;
        const seq = requestSeq.current;
        const params = new URLSearchParams({ uuid: userUUID, role: userRole, page_no: pageNo });
        if (dir) params.set('direction', dir);
        if (cat) params.set('category', cat);
        const url = get_api_url(
          `/v2/notifications/cms/email/timeline?${params.toString()}`,
          false,
          true
        );

        get_api(url, true)
          .then((res) => {
            if (seq !== requestSeq.current) return;
            const to_address = res?.data?.data[0]?.email_details?.to_address;
            if (to_address) {
              const index = to_address?.indexOf('<');
              if (index !== -1) {
                const extractedName = to_address.slice(0, index).trim();
                setPageTitle(`Emails timeline for ${userRole} ${extractedName}`);
              }
            }
            setTimelineData(sortData(res?.data?.data || []));
            // An empty filtered result reports 0 pages; 1 keeps "Next" disabled.
            setTotalPages(Math.max(1, res?.data?.total_pages || 0));
            setLoading(false);
          })
          .catch((err) => {
            if (seq !== requestSeq.current) return;
            // Clear the old rows: left on screen they read as matches for the
            // filter that just failed.
            setTimelineData([]);
            const defaultError = 'Some error occured. Please contact dev team.';
            let err_message;
            try {
              err_message = err?.response?.data?.error ?? defaultError;
            } catch (err) {
              err_message = defaultError;
            }
            setNotificationVariable(true, 'error', err_message);
            setLoading(false);
          });
      }
    } catch (e) {
      setNotificationVariable(true, 'error', err_message);
      setLoading(false);
    }
  };

  useEffect(() => {
    const extractUserRoleAndUUID = () => {
      const splitPath = location.pathname?.split('/');
      if (splitPath?.[2] && splitPath?.[3]) {
        setUserRole(splitPath[2]);
        setUserUUID(splitPath[3]);
        setLoading(true);
      } else {
        goToPath('/');
      }
    };
    try {
      extractUserRoleAndUUID();
    } catch {
      goToPath('/');
    }
  }, [location.pathname]);

  useEffect(() => {
    loadData();
  }, [userUUID, userRole]);

  // /v2/support/search finds the person's tickets (two searches, see
  // care_rows.js searchParamSets), then each thread is read from
  // /v2/support/conversations/<uuid>, the same calls the Support Inbox makes.
  // None of them writes: marking a thread read is a separate POST.
  const loadCareData = () => {
    // Already loaded, or nothing to search by. Still clear `loading`: the
    // caller just retired the in-flight timeline request that would have.
    if (careThreads || !contactEmail) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setCareError('');
    // Shares the timeline's counter: leaving Care team before this lands bumps
    // it, and a late response must not then clear the spinner or post an error
    // over the category now on screen. The threads are still cached either way.
    const seq = requestSeq.current;
    const isCurrent = () => seq === requestSeq.current;
    // 100 is the search endpoint's cap (goserver api_search.go maxSearchLimit).
    // Fuzzy matches on other addresses count against it, so a person with more
    // than 100 hits on one search would lose threads; page the search if that
    // ever shows up.
    const searchUrl = (params) =>
      get_api_url(`/v2/support/search?${new URLSearchParams(params).toString()}`, false, true);
    Promise.all(searchParamSets(userRole, contactEmail).map((p) => get_api(searchUrl(p), true)))
      .then((responses) => {
        const hits = uniqueHits(responses.map((r) => r?.data?.data?.conversations || []));
        // allSettled: one thread failing to load (deleted between search and
        // fetch, a single 5xx) must not hide every other ticket.
        return Promise.allSettled(
          hits.map((hit) =>
            get_api(get_api_url(`/v2/support/conversations/${hit.uuid}`, false, true), true).then(
              (r) => r?.data?.data
            )
          )
        );
      })
      .then((results) => {
        const me = { role: userRole, uuid: userUUID, email: contactEmail };
        const threads = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
        const failed = results.length - threads.length;
        setCareThreads(threads.filter((t) => ownsConversation(t?.conversation, me)));
        // Set before the guard: these threads are cached and never refetched,
        // so a partial failure that lands while another category is on screen
        // must still be shown when Care team is opened again. careError renders
        // only in the Care team view, so setting it now shows nothing elsewhere.
        setCareError(failed ? `${failed} Support Inbox ticket(s) could not be loaded.` : '');
        if (!isCurrent()) return;
        setLoading(false);
      })
      .catch((err) => {
        if (!isCurrent()) return;
        setCareError(err?.response?.data?.reason || 'Could not load Support Inbox emails.');
        setLoading(false);
      });
  };

  const changeFilters = (dir, cat) => {
    setDirection(dir);
    setCategory(cat);
    setCurrentPage(1);
    if (cat === CARE) {
      // Retire any timeline request still in flight, or its late response would
      // flip `loading` off while the Care team threads are still loading.
      requestSeq.current += 1;
      loadCareData();
    } else {
      loadData(1, dir, cat);
    }
  };

  const careFiltered = careRows(careThreads, direction);

  const toggleEmailTrack = (idx) => {
    setSelectedIdx(idx);
    setShowStatusLog(!showStatusLog);
  };

  const toggleRawData = (idx) => {
    setSelectedIdx(idx);
    setShowRawData(!showRawData);
    if (!showRawData) {
      setRawDataSearchInput('');
    }
  };

  const toggleShowEmail = (idx) => {
    setSelectedIdx(idx);
    setShowEmail(!showEmail);
  };

  const handleRawdataSearch = (e) => {
    const { value } = e.target;
    setRawDataSearchInput(value);
  };

  const renderRawData = () => {
    if (showRawData) {
      const { raw_data } = timelineData[selectedIdx];
      if (raw_data) {
        const object_keys = Object.keys(raw_data).sort();
        const filtered_keys = object_keys.filter(
          (k) => k.toLowerCase().indexOf(rawDataSearchInput.toLowerCase()) !== -1
        );

        return (
          <div style={{ height: '80vh' }}>
            <CustomInput
              label="Search key value"
              style={{ width: '500px' }}
              name="rawDataSearchInput"
              onChange={handleRawdataSearch}
            />
            <div className="cus-table" style={{ marginBottom: '20px' }}>
              <div className="ct-row ct-h">
                <div className="ct-col ct-hf ct-la ct-pl-10">Key</div>
                <div className="ct-col ct-la ct-pl-10">Value</div>
              </div>
              {filtered_keys.map((item, index) => (
                <div className="ct-row" key={index}>
                  <div className="ct-col ct-hf ct-bw ct-sm-font ct-la ct-pl-10">
                    <HighlightSearch
                      full_text={item}
                      search_text={rawDataSearchInput?.toLowerCase()}
                    />
                  </div>
                  <div className="ct-col ct-bw ct-sm-font ct-la ct-pl-10">
                    {raw_data[item] !== '' && raw_data[item] !== null ? raw_data[item] : '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }
      return <center>No raw data found</center>;
    }
  };

  const renderEmailTrack = () => {
    if (showStatusLog) {
      const { email_trail } = timelineData[selectedIdx];
      if (email_trail) {
        return (
          <div style={{ height: '80vh', position: 'relative' }}>
            <div style={{ position: 'fixed', bottom: '10px', right: '10px' }}>
              <StatusCodeLegend />
            </div>
            <h1>
              <center>Tracking details</center>
            </h1>
            <div className="max-w-xl mx-auto p-8">
              <div className="relative">
                {email_trail.map((item, index) => (
                  <div key={index} className="flex items-baseline last:mb-0">
                    <div className="flex-1 text-xs text-right pr-8">
                      {moment(item.time).tz(iana_timezone).format(day_format_full_year)}{' '}
                      {moment(item.time).tz(iana_timezone).format(time_format)}
                    </div>
                    <div className="relative flex-1 pl-8 text-sm  pb-6">
                      <p style={{ color: item.color, fontWeight: 600 }}>{item.status}</p>
                      <ul className="mt-2 text-xs">
                        {item?.ip_address ? (
                          <li>
                            <b>IP Address:</b> {item.ip_address}
                          </li>
                        ) : (
                          ''
                        )}
                        {item?.user_agent ? (
                          <li>
                            <b>User Agent:</b> {item.user_agent}
                          </li>
                        ) : (
                          ''
                        )}
                        {userGeoInfos[item.ip_address] ? (
                          <>
                            {userGeoInfos[item.ip_address]?.cn_iso ? (
                              <li>
                                <b>Country code:</b> {userGeoInfos[item.ip_address].cn_iso}
                              </li>
                            ) : (
                              ''
                            )}
                            {userGeoInfos[item.ip_address]?.cn_nm ? (
                              <li>
                                <b>City name:</b> {userGeoInfos[item.ip_address].cn_nm}
                              </li>
                            ) : (
                              ''
                            )}
                            {userGeoInfos[item.ip_address]?.ct_nm ? (
                              <li>
                                <b>Country name:</b> {userGeoInfos[item.ip_address].ct_nm}
                              </li>
                            ) : (
                              ''
                            )}
                            {userGeoInfos[item.ip_address]?.geo ? (
                              <li>
                                <b>Latitude & Longitude:</b> {userGeoInfos[item.ip_address].geo}
                              </li>
                            ) : (
                              ''
                            )}
                            {userGeoInfos[item.ip_address]?.tz ? (
                              <li>
                                <b>Timezone:</b> {userGeoInfos[item.ip_address].tz}
                              </li>
                            ) : (
                              ''
                            )}
                          </>
                        ) : null}
                        {item?.ip_address && !userGeoInfos[item.ip_address] ? (
                          <div
                            className="relative inline-block group"
                            onClick={() => fetchUserGeoInfo(item.ip_address, index)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width="24"
                              height="24"
                              className="cursor-pointer"
                            >
                              <g fill="none" stroke="red" strokeWidth="2">
                                <path d="M12 2C8.134 2 5 5.134 5 8c0 4.5 7 11 7 11s7-6.5 7-11c0-2.866-3.134-6-7-6z" />
                                <circle cx="12" cy="8" r="2" fill="red" />
                              </g>
                            </svg>
                            <span className="absolute bottom-[-30px] left-0 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 text-sm bg-black text-white px-2 py-1 rounded-md whitespace-nowrap transition-opacity">
                              Fetch user geo details
                            </span>
                          </div>
                        ) : null}
                      </ul>
                      <div className="absolute top-[17px] -left-[7px] h-full">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        {index !== email_trail.length - 1 && (
                          <div
                            className={`absolute w-px h-full ${index === email_trail.length - 1 ? '' : 'bg-line'} left-2 top-4`}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }
    }
  };

  const renderEmailView = () => {
    if (showEmail) {
      const { email_details, email_tag, raw_data } = timelineData[selectedIdx];
      if (email_details) {
        return (
          <div style={{ height: '80vh' }}>
            <div className="max-w-2xl mx-auto p-6 bg-surface-card">
              <div className="flex items-center gap-2 mb-4">
                {/* <Calendar className="w-5 h-5 text-state-info" /> */}
                <h1 className="text-xl font-semibold">{timelineData[selectedIdx]?.event_name}</h1>
                <span className="ml-auto text-content-muted text-sm">{email_details?.date}</span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex">
                  <span className="w-20 text-content-secondary">From:</span>
                  <span>{email_details?.from_address ? email_details?.from_address : '-'}</span>
                </div>
                <div className="flex">
                  <span className="w-20 text-content-secondary">To:</span>
                  <span>{email_details?.to_address ? email_details?.to_address : '-'}</span>
                </div>
                <div className="flex">
                  <span className="w-20 text-content-secondary">CC:</span>
                  <span>{email_details?.cc_address !== '' ? email_details?.cc_addres : '-'}</span>
                </div>
                <div className="flex">
                  <span className="w-20 text-content-secondary">BCC:</span>
                  <span>{email_details?.bcc_address ? email_details?.bcc_address : '-'}</span>
                </div>
                <div className="flex">
                  <span className="w-20 text-content-secondary">Subject:</span>
                  <span>{email_details?.subject ? email_details?.subject : '-'}</span>
                </div>
              </div>
              <div className="space-y-4">
                Description:
                <FrameWrapper html={email_details?.body} />
              </div>
              {email_tag === 'zendesk' ? (
                <>
                  <div className="space-y-4 mt-6 mb-2 underline">Zendesk information:</div>
                  <li className="text-sm">
                    <strong className="font-medium">Zendesk Ticket ID: </strong>
                    {raw_data?.zendesk_ticket_id ? (
                      <a
                        href={`https://mytcare.zendesk.com/agent/tickets/${raw_data?.zendesk_ticket_id}`}
                        target="_blank"
                        className="text-state-info underline"
                        rel="noreferrer"
                      >
                        {raw_data?.zendesk_ticket_id ? `${raw_data?.zendesk_ticket_id}` : '-'}
                      </a>
                    ) : (
                      '-'
                    )}
                  </li>
                  {raw_data?.zendesk_latest_comment ? (
                    <li className="text-sm">
                      <strong className="font-medium">Zendesk Latest Comment:</strong>{' '}
                      {raw_data?.zendesk_latest_comment}
                    </li>
                  ) : null}
                </>
              ) : null}
              {email_details?.calendar ? (
                <div className="flex items-center gap-2 mt-6 text-state-info">
                  {/* <Download className="w-4 h-4" /> */}
                  <span className="text-sm">
                    <EventViewer icsString={email_details?.calendar} />
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        );
      }
    }
  };

  // author_email is written only for inbound mail (goserver support
  // inbound.go); agent replies carry author_cms_uuid instead, and resolving it
  // to a name needs the inbox's agent list, which this page does not load.
  const careAuthor = (message) => {
    if (message.author_type === 'system') return 'System (auto reply)';
    if (message.author_type === 'agent') return 'Care agent';
    return message.author_email || '-';
  };

  const renderCareEmailView = () => {
    if (!showEmail || category !== CARE) return null;
    const row = carePage(careFiltered, currentPage)[selectedIdx];
    if (!row) return null;
    const { conversation, message } = row;
    // Same rule as the Support Inbox bubble: a non-blank body_html is rich
    // text, otherwise body_text (plain-text inbound mail stores an empty
    // body_html). Both are untrusted email, so HTML goes through the inbox's
    // render sanitizer before reaching FrameWrapper's same-origin iframe, and
    // text is escaped by textToHtmlBody.
    const html = message.body_html?.trim()
      ? cleanMessageHtml(message.body_html, message.attachments)
      : textToHtmlBody(message.body_text);
    return (
      <div style={{ height: '80vh' }}>
        <div className="max-w-2xl mx-auto p-6 bg-surface-card">
          <div className="flex items-center gap-2 mb-4">
            <h1 className="text-xl font-semibold">{message.subject || conversation.subject}</h1>
            <span className="ml-auto text-content-muted text-sm">
              {moment(message.created_at).tz(iana_timezone).format(day_format_full_year)}{' '}
              {moment(message.created_at).tz(iana_timezone).format(time_format)}
            </span>
          </div>
          <div className="space-y-3 mb-6">
            <div className="flex">
              <span className="w-24 text-content-secondary">Direction:</span>
              <span>{message.direction === 'inbound' ? 'Incoming' : 'Outgoing'}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-content-secondary">Author:</span>
              <span>{careAuthor(message)}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-content-secondary">Contact:</span>
              <span>{conversation.contact?.email || '-'}</span>
            </div>
            <div className="flex">
              <span className="w-24 text-content-secondary">Ticket:</span>
              <a
                href={`/support?tkt=${encodeURIComponent(conversation.uuid)}`}
                target="_blank"
                rel="noreferrer"
                className="text-state-info underline"
              >
                #{conversation.ticket_number} Open in Support Inbox
              </a>
            </div>
          </div>
          <div className="space-y-4">
            Description:
            <FrameWrapper html={html} />
          </div>
        </div>
      </div>
    );
  };

  const renderCareRow = ({ conversation, message }, idx) => (
    <div className="ct-row" key={message.uuid}>
      <div className="ct-col ct-sm-font relative">
        {message.subject || conversation.subject}
        <br />
        <span className="bg-state-info-bg text-state-info-text text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">
          Support #{conversation.ticket_number}
        </span>
      </div>
      <div className="ct-col ct-sm-font">
        {message.direction === 'inbound' ? 'Incoming' : 'Outgoing'}
        {message.author_type === 'system' ? ' (auto reply)' : ''}
      </div>
      <div className="ct-col ct-sm-font">
        {moment(message.created_at).tz(iana_timezone).format(day_format_full_year)}{' '}
        {moment(message.created_at).tz(iana_timezone).format(time_format)}
      </div>
      <div className="ct-col ct-sm-font ct-si">
        <button
          type="button"
          aria-label="View email"
          className="bg-transparent border-transparent"
          onClick={() => toggleShowEmail(idx)}
        >
          <img
            src="https://cloudimages.myyogateacher.com/dgerdfai4/image/upload/v1729674308/website/eye_1.svg"
            alt=""
            style={{ width: '26px', cursor: 'pointer' }}
          />
        </button>
      </div>
      <div className="ct-col ct-sm-font">
        <a
          href={`/support?tkt=${encodeURIComponent(conversation.uuid)}`}
          target="_blank"
          rel="noreferrer"
          className="text-state-info underline"
        >
          Open
        </a>
      </div>
    </div>
  );

  const renderCareData = () => {
    if (loading) return <CustomLoading />;
    if (!contactEmail) {
      return (
        <center className="text-content-muted">
          Open Emails Timeline from the {userRole} profile to load Care team emails.
        </center>
      );
    }
    // With no threads loaded the error is the whole answer; after a partial
    // load it sits above the tickets that did load.
    if (careError && !careThreads) {
      return <center className="text-state-error">{careError}</center>;
    }
    const rows = carePage(careFiltered, currentPage);
    return (
      <div className="cus-table">
        {careError ? <center className="text-state-error">{careError}</center> : null}
        <div className="ct-row ct-h">
          <div className="ct-col ct-sm">Subject</div>
          <div className="ct-col ct-sm">Direction</div>
          <div className="ct-col ct-sm">Created date</div>
          <div className="ct-col ct-sm">View Email</div>
          <div className="ct-col ct-sm">Support Inbox</div>
        </div>
        {rows.length ? (
          rows.map(renderCareRow)
        ) : (
          <center className="text-content-muted">No emails found</center>
        )}
      </div>
    );
  };

  const renderFilters = () => (
    <div className="flex flex-wrap gap-4 mb-4">
      <CustomSelect
        label="Category"
        name="category"
        defaultOption="Transactional + Zendesk"
        options={CATEGORY_OPTIONS}
        value={category}
        onChange={(e) => changeFilters(direction, e.target.value)}
      />
      <CustomSelect
        label="Direction"
        name="direction"
        defaultOption="All directions"
        options={DIRECTION_OPTIONS}
        value={direction}
        onChange={(e) => changeFilters(e.target.value, category)}
      />
    </div>
  );

  const pageCount = category === CARE ? careTotalPages(careFiltered) : totalPages;

  const getVisiblePages = () => {
    const start = Math.max(1, currentPage - 3);
    const length = Math.min(4, pageCount);
    return Array.from({ length }, (_, i) => start + i);
  };

  const renderPagination = () => {
    if (!loading) {
      return (
        <div className="flex items-center justify-center space-x-2 mt-4">
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded text-content-primary disabled:bg-surface-sunken disabled:text-content-muted bg-surface-card border border-line hover:bg-surface-sunken"
          >
            Previous
          </button>

          {getVisiblePages().map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 rounded ${
                currentPage === page
                  ? 'bg-state-info-solid text-content-on-nav'
                  : 'bg-surface-card text-content-primary border border-line hover:bg-surface-sunken'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(Math.min(pageCount, currentPage + 1))}
            disabled={currentPage >= pageCount}
            className="px-3 py-1 text-content-primary rounded disabled:bg-surface-sunken disabled:text-content-muted bg-surface-card border border-line hover:bg-surface-sunken"
          >
            Next
          </button>
        </div>
      );
    }
  };

  const renderSingleRow = (data, idx) => {
    const { event_name, timestamp, email_status, raw_data, email_details } = data;
    return (
      <div className="ct-row" id={raw_data?.email_tracking_uuid}>
        {/* <div className="ct-col ct-sm-font">{event_name}</div> */}
        <div className="ct-col ct-sm-font relative">
          {event_name || email_details?.subject}
          <br />
          {data?.email_tag === 'zendesk' ? (
            <span className="bg-gray-600  text-white top-0 left-0 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">
              Zendesk
            </span>
          ) : (
            <span className="bg-green-100 text-green-950 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">
              Transactional
            </span>
          )}
        </div>
        <div className="ct-col ct-sm-font">{email_status}</div>
        <div className="ct-col ct-sm-font">
          {timestamp !== '' ? (
            <>
              {moment(timestamp).tz(iana_timezone).format(day_format_full_year)}{' '}
              {moment(timestamp).tz(iana_timezone).format(time_format)}
            </>
          ) : (
            'NA'
          )}
        </div>
        <div className="ct-col ct-sm-font ct-si" onClick={() => toggleShowEmail(idx)}>
          <img
            src="https://cloudimages.myyogateacher.com/dgerdfai4/image/upload/v1729674308/website/eye_1.svg"
            style={{ width: '26px', cursor: 'pointer' }}
          />
        </div>
        <div
          className="ct-col ct-sm-font ct-si"
          style={{ cursor: 'pointer' }}
          onClick={() => toggleEmailTrack(idx)}
        >
          <img src="https://images.myyogateacher.com/icons/ic_info_blue.png" />
        </div>
        <div className="ct-col ct-sm-font" onClick={() => toggleRawData(idx)}>
          <button className="text-content-secondary hover:text-content-primary bg-transparent border-transparent">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderData = () => {
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className="cus-table">
        <div className="ct-row ct-h">
          <div className="ct-col ct-sm">Event name</div>
          <div className="ct-col ct-sm">Email status</div>
          <div className="ct-col ct-sm">Created date</div>
          <div className="ct-col ct-sm">View Email</div>
          <div className="ct-col ct-sm">Tracking details</div>
          <div className="ct-col ct-sm">Raw data</div>
        </div>
        {timelineData.length !== 0 ? (
          timelineData.map(renderSingleRow)
        ) : (
          <center className="text-content-muted">No emails found</center>
        )}
      </div>
    );
  };

  const setNotificationVariable = (show, type, message) => {
    dispatch(actions.set_notification_variable(show, type, message));
  };

  return (
    <CommonHeader loading={loading} title={pageTitle} show_back>
      {renderFilters()}
      {category === CARE ? renderCareData() : renderData()}
      {renderPagination()}
      <CustomModal show={showStatusLog} close={toggleEmailTrack}>
        {renderEmailTrack()}
      </CustomModal>
      <CustomModal show={showRawData} close={toggleRawData}>
        {renderRawData()}
      </CustomModal>
      <CustomModal show={showEmail} close={toggleShowEmail}>
        {category === CARE ? renderCareEmailView() : renderEmailView()}
      </CustomModal>
    </CommonHeader>
  );
}

export default EmailTimeline;
